/**
 * Airline Logo Registry
 * Which airlines have a logo stored on the admin "Airlines" page, and each logo's
 * version. Filled by useAllowedAirlines from GET /api/airlines/allowed.
 *
 * Until a non-empty list has been received (now, or on an earlier run via
 * localStorage) boards fall back to the logos bundled in Airline_Icons.json, so a
 * kiosk that can't reach the server never loses its logos.
 */

const STORAGE_KEY = 'fids_airline_logo_versions';

const normalizeVersions = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const entries = Object.entries(value)
    .map(([code, version]) => [String(code).trim().toUpperCase(), typeof version === 'string' ? version.trim() : ''])
    .filter(([code, version]) => /^[A-Z0-9]{2,3}$/.test(code) && /^[A-Za-z0-9_-]{1,64}$/.test(version));
  return entries.length > 0 ? Object.fromEntries(entries) : null;
};

const readCachedVersions = () => {
  try {
    return normalizeVersions(JSON.parse(localStorage.getItem(STORAGE_KEY)));
  } catch (err) {
    return null;
  }
};

let versions = readCachedVersions();

/**
 * Replace the registry with the server's list. An empty or invalid list is ignored,
 * so boards keep their current logos rather than losing them all.
 * @returns {boolean} whether the registry was updated
 */
export function setLogoVersions(value) {
  const next = normalizeVersions(value);
  if (!next) return false;

  versions = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (err) {
    // Storage unavailable - the in-memory registry still applies
  }
  return true;
}

export const hasLogoRegistry = () => versions !== null;

export const getLogoVersion = (airlineCode) =>
  (versions && airlineCode ? versions[String(airlineCode).trim().toUpperCase()] || null : null);

// Re-read from localStorage (used by tests)
export function resetLogoRegistry() {
  versions = readCachedVersions();
}
